<template>
  <div class="flex items-center space-x-4">
    <lf-input
      v-bind="minAttributes"
      v-maska
      :value="min"
      :min="minAllowed"
      @key-released="updateValue"
    />
    <span class="mb-5">&horbar;</span>
    <lf-input
      v-bind="maxAttributes"
      v-maska
      :value="max"
      :max="maxAllowed"
      @key-released="updateValue($event, true)"
    />
  </div>
</template>
<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

interface InputOptions {
  class: string;
  name: string;
  placeholder: string;
  type: string;
  hasError?: boolean;
  ["data-maska"]?: string;
}

type PropsInputOptions = Omit<Partial<InputOptions>, "name" | "placeholder">;

const { t } = useI18n();

const getInitialOptions = (): Pick<
  InputOptions,
  "class" | "type" | "data-maska"
> => ({
  class: "max-w-50",
  type: "number",
  ["data-maska"]: "####",
});

interface Props {
  min: number | null;
  max: number | null;
  minAllowed?: number;
  maxAllowed?: number;
  minOptions?: PropsInputOptions;
  maxOptions?: PropsInputOptions;
}

const props = withDefaults(defineProps<Props>(), {
  minOptions: () => ({
    class: "max-w-50",
    type: "number",
    ["data-maska"]: "####",
  }),
  maxOptions: () => ({
    class: "max-w-50",
    type: "number",
    ["data-maska"]: "####",
  }),
});

const emit = defineEmits<{
  "update:min": [value: number | null];
  "update:max": [value: number | null];
}>();

const minAttributes = computed<InputOptions>(() => {
  return {
    ...getInitialOptions(),
    ...props.minOptions,
    name: "min",
    placeholder: t("COMMON.MIN"),
  };
});

const maxAttributes = computed<InputOptions>(() => {
  return {
    ...getInitialOptions(),
    ...props.maxOptions,
    name: "max",
    placeholder: t("COMMON.MAX"),
  };
});

const updateValue = (value: string, settingMax = false) => {
  const numVal = value ? Number(value) : null;
  settingMax ? emit("update:max", numVal) : emit("update:min", numVal);
};
</script>
